/**
 * SCD Lot 1 — retailSlice : boucle retail B2C mono-magasin.
 *
 * Boucle quotidienne (tickRetail, appelée depuis coreSlice.nextDay) :
 *   1. Réception des commandes fournisseurs arrivées à échéance (stock += qty, CUMP recalculé).
 *   2. Ventes par famille : demande = trafic × conversion × panier × saison × élasticité-prix.
 *      Rupture = demande non servie (ventes perdues, CA manqué compté).
 *   3. Surstock (> 45 j de couverture) → démarque quotidienne (1 % du surplus jeté au CUMP).
 *   4. Paiements fournisseurs dus (30/60 j) → décaissements.
 *   5. Loyer + charges du magasin (quotidiens) + redevance franchise (% du CA du jour).
 * Retourne { ca, decaissements } — intégrés à dayRevenue / dayExpenses du moteur SCD.
 */
import {
  getSecteur, getPositionnement, getModele, getPdvForSecteur,
  prixConseille, priceAdjustment, demandeBaseJour,
  remisePourQty, prixUnitaireNet, couvertureJours, getFournisseur, getFournisseursBySecteur,
  getRelationFournisseur, relationNiveau, probaRetardLivraison, fournisseurCouvre,
  grosseCommandeSeuil, RELATION_DEFAUT, REL_LIVRAISON, REL_GROSSE_COMMANDE,
  REL_RETARD, REL_ALEA, ALEA_PROBA_SUR_RETARD,
  SEUIL_SURSTOCK_JOURS, TAUX_DEMARQUE_JOUR,
  getMetier, CONTRATS_SPECIAUX, FORMATION, computeEquipeEffects,
  TAUX_FRAIS_CB, PART_CB, TASCOM_SEUIL_M2, TASCOM_EUR_M2_AN, INDEXATION_BAIL,
} from '../../utils/retail';
import {
  defaultMarketing, computeMarketingEffects, indexPrixMoyen,
  getLevier, PROMO_CONFIG, applyRetailEventEffect,
} from '../../utils/retailMarketing';
import { getMecanique, prochaineMecanique, delaiProchaineMecanique } from '../../utils/mecaniques';
import { tirerEvenementVille, evenementLocalActif, PROBA_EVENEMENT_VILLE_JOUR } from '../../utils/evenementsVilles';
import { VILLE_PRINCIPAL_DEFAUT, typesVille, bonusDominationPct, bonusReseauGeo, villeDuMagasin, VILLES, magasinsParVille } from '../../utils/villes';
import {
  ENSEIGNES_CONCURRENTES, getEnseigne, malusConcurrence, concurrentDansVille,
  estimationFonds, prixMurs, valeurMurs, probaOffreJour, genererOffre,
  DUREE_HYPOTHEQUE_MOIS, TAUX_HYPOTHEQUE, LIQUIDATION_JOURS, INDEMNITE_BAIL_MOIS,
} from '../../utils/parc';
import {
  defaultMagasinZones, defaultEcommerce,
  computeMagasinEffects, computeReseauEffects, computeEcommerceTick,
  getTypeExpansion, getPalier, EC_ACQUISITION, EC_LOGISTIQUE,
} from '../../utils/magasin';
import {
  getAmelioration, peutAcheter, computeAmelFx, fermeturePartielle,
  appliquerChantiers, CHANTIER_TRAFIC_MULT,
} from '../../utils/ameliorations';
import { buildMagasinFromTunnel } from '../../utils/ouverture';
import {
  computeMerchFx, computeFacing, computeParcours, coutFacing, coutParcours,
  merchSecteur, typeMagasinSecteur, multGlobalSecteur, previsionSaison, secteursReseau, suggererFacing,
} from '../../utils/merchandising';
import {
  etatFacingNegocie, genererOffreFacing, forcerVerrous, nomSecteur, BONUS_RELATION_ACCEPT,
} from '../../utils/facingNegocie';
import {
  COUT_STRUCTURATION_FRANCHISE, DROITS_ENTREE, REDEVANCES_PCT, franchiseDebloquee,
  delaiProchaineCandidature, genererCandidatFranchise, PROBA_RESILIATION_MENSUELLE,
  AIDE_FRANCHISE_CHOIX, probaAideFranchiseJour,
} from '../../utils/franchise';

const PRENOMS = ['Camille', 'Jules', 'Inès', 'Hugo', 'Léa', 'Nathan', 'Chloé', 'Louis', 'Manon', 'Sacha', 'Zoé', 'Rayan', 'Alice', 'Tom', 'Nora'];
const NOMS = ['Berthelot', 'Renard', 'Delcourt', 'Maréchal', 'Girard', 'Lambert', 'Fabre', 'Roussel', 'Perrot', 'Chevalier', 'Moulin', 'Aubry'];
const randomNom = () => `${PRENOMS[Math.floor(Math.random() * PRENOMS.length)]} ${NOMS[Math.floor(Math.random() * NOMS.length)]}`;

const genId = () => `cmd-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/* ── 02/07 — Réapprovisionnement automatique ──────────────────────────────
 * Consigne par famille : { seuil, qty, prixMax|null, alerted }.
 * Chaque jour (fin de tick) : stock ≤ seuil + aucune commande en cours sur
 * la famille → commande auto chez le fournisseur le moins cher qui couvre
 * la famille (prix net effectif, relation + remises groupe incluses).
 * Prix max dépassé ou trésorerie insuffisante → alerte messagerie (1 fois). */
/**
 * 05/07 — Facing négocié : expire les offres, encaisse la coopération
 * commerciale des contrats, clôture les contrats échus, propose une offre
 * les 1er et 15 du mois. Retourne l'encaissement du jour (€).
 */
const executerFacingNegocie = (get, set, { day, totalDays }) => {
  const s = get();
  const r = s.retail;
  if (!r) return 0;
  const etat = etatFacingNegocie(r);
  const jour = totalDays + 1;
  const push = (payload) => s.pushCustomMessage?.(payload);
  let encaisse = 0;
  const contrats = [];
  etat.contrats.forEach((c) => {
    encaisse += c.montantMensuel / 30;
    if (jour >= c.finJour) {
      push({ senderType: 'fournisseur', senderName: c.fournisseurNom, tone: 'info', kind: 'info', subject: `🏁 Fin de la tête de gondole ${c.famNom}`, body: `Notre contrat de coopération commerciale (${c.facings} facings ${c.famNom} au niveau des yeux) arrive à son terme : ${c.montantMensuel} €/mois versés pendant ${c.dureeJours} jours. Les emplacements sont libérés — à vous de réorganiser le rayon ${nomSecteur(c.secteurId)}.`, ttlDays: 10 });
    } else contrats.push(c);
  });
  let offres = etat.offres.filter(o => jour < o.expireJour);
  if ((day === 1 || day === 15) && totalDays >= 10 && !offres.length && Math.random() < 0.6) {
    const secteurs = secteursReseau(r);
    const secteurId = secteurs[Math.floor(Math.random() * secteurs.length)];
    const offre = genererOffreFacing({ ...r, facingNegocie: { ...etat, contrats } }, secteurId, { totalDays: jour });
    if (offre) {
      offres = [...offres, offre];
      push({ senderType: 'fournisseur', senderName: offre.fournisseurNom, tone: 'info', kind: 'info', subject: `🛒 Proposition : tête de gondole ${offre.famNom} payée ${offre.montantMensuel} €/mois`, body: `Nous vous proposons de financer ${offre.facings} facings ${offre.famNom} au niveau des yeux de votre rayon ${nomSecteur(secteurId)} pendant ${offre.dureeJours} jours, contre ${offre.montantMensuel} €/mois de coopération commerciale. À vous d'arbitrer : l'argent est garanti, mais ces emplacements ne seront plus disponibles pour vos familles les plus rentables. Offre valable ${offre.expireJour - jour} jours — page Merchandising, onglet Facing.`, ttlDays: 7 });
    }
  }
  const arrondi = Math.round(encaisse * 100) / 100;
  set({ retail: { ...get().retail, facingNegocie: { offres, contrats, cumulEur: Math.round(((etat.cumulEur || 0) + arrondi) * 100) / 100 } } });
  return arrondi;
};

const executerReaproAuto = (get, set) => {
  const s = get();
  const r = s.retail;
  if (!r?.magasin) return;
  const consignes = r.reapro || {};
  const entries = Object.entries(consignes).filter(([, c]) => c);
  if (entries.length === 0) return;
  const msg = (payload) => { if (typeof get().pushCustomMessage === 'function') get().pushCustomMessage(payload); };
  const sec = getSecteur(r.secteur);
  const remiseGroupe = Math.min(0.12,
    (typeof get().getSupportAchatDiscount === 'function' ? get().getSupportAchatDiscount() : 0)
    + ((s.activeInvestments || {})['grp-centrale'] ? 0.03 : 0));
  const maj = {};
  const setAlerted = (famId, c, val) => { if ((c.alerted || false) !== val) maj[famId] = { ...c, alerted: val }; };

  entries.forEach(([famId, c]) => {
    const fam = sec.familles.find(f => f.id === famId);
    if (!fam) return;
    const stQty = r.stock?.[famId]?.qty || 0;
    if (stQty > c.seuil) { setAlerted(famId, c, false); return; }
    if (r.commandes.some(cmd => cmd.famId === famId && !cmd.recu)) return;

    const options = getFournisseursBySecteur(r.secteur)
      .filter(f => fournisseurCouvre(f, famId))
      .map(f => {
        const relation = getRelationFournisseur(r, f.id);
        const pu = Math.round(prixUnitaireNet(fam, f, c.qty, r.modele, relation) * (1 - remiseGroupe) * 100) / 100;
        return { f, pu };
      })
      .sort((a, b) => a.pu - b.pu);
    const best = options[0];
    if (!best) return;

    if (c.prixMax != null && best.pu > c.prixMax) {
      if (!c.alerted) {
        msg({ senderType: 'system', senderName: 'SCD — Réapro auto', tone: 'urgent', kind: 'info', ttlDays: 5,
          subject: `⚠️ Réapro bloqué — ${fam.nom}`,
          body: `Stock à ${stQty} u. (seuil ${c.seuil}) mais aucun fournisseur ne respecte votre prix max de ${c.prixMax.toFixed(2)} € (meilleur prix : ${best.pu.toFixed(2)} € chez ${best.f.nom}). Ajustez la consigne ou commandez manuellement.` });
        setAlerted(famId, c, true);
      }
      return;
    }
    const montant = Math.round(best.pu * c.qty * 100) / 100;
    if ((get().treasury || 0) < montant) {
      if (!c.alerted) {
        msg({ senderType: 'system', senderName: 'SCD — Réapro auto', tone: 'urgent', kind: 'info', ttlDays: 5,
          subject: `⚠️ Réapro bloqué — ${fam.nom}`,
          body: `Stock à ${stQty} u. (seuil ${c.seuil}) mais la trésorerie est insuffisante pour la commande auto (${montant.toLocaleString('fr-FR')} €).` });
        setAlerted(famId, c, true);
      }
      return;
    }
    const res = get().passerCommande({ fournisseur: best.f, famId, qty: c.qty });
    if (res?.ok) {
      msg({ senderType: 'system', senderName: 'SCD — Réapro auto', tone: 'positive', kind: 'info', ttlDays: 4,
        subject: `🔄 Réapro auto — ${fam.nom}`,
        body: `Stock passé sous le seuil (${stQty}/${c.seuil} u.) : commande automatique de ${c.qty} × ${fam.nom} chez ${best.f.nom} (${best.pu.toFixed(2)} €/u., livraison J${res.commande.jourLivraison}).` });
      setAlerted(famId, c, false);
    } else if (res?.reason === 'capacite' && !c.alerted) {
      msg({ senderType: 'system', senderName: 'SCD — Réapro auto', tone: 'urgent', kind: 'info', ttlDays: 5,
        subject: `⚠️ Réapro bloqué — ${fam.nom}`,
        body: `La commande auto de ${c.qty} u. dépasse la capacité de stockage disponible. Réduisez la quantité de la consigne ou agrandissez la réserve.` });
      setAlerted(famId, c, true);
    }
  });

  if (Object.keys(maj).length > 0) {
    const cur = get().retail;
    set({ retail: { ...cur, reapro: { ...(cur.reapro || {}), ...maj } } });
  }
};

// 04/07 — merchandising par secteur : migre l'ancien format { facing, parcours } (= secteur principal).
const migrerMerch = (r) => {
  const m = r.merchandising || {};
  if ('facing' in m || 'parcours' in m) return { [r.secteur]: { facing: m.facing || null, parcours: m.parcours || null } };
  return m;
};

export const createRetailSlice = (set, get) => ({
  retail: null,

  /**
   * Bootstrap de la boucle retail à la création de partie.
   * Déduit de la trésorerie : droits d'entrée franchise + valeur du stock initial.
   */
  initRetail: ({ secteur, positionnement = 'coeur', modele = 'independant', magasinConfig = null }) => {
    const sec = getSecteur(secteur);
    if (!sec) return;
    const mod = getModele(modele);
    const pdv = getPdvForSecteur(secteur);

    const assortiment = {};
    const stock = {};
    const ventes7 = {};
    let stockValue = 0;
    sec.familles.forEach(f => {
      const conseille = prixConseille(f, positionnement);
      assortiment[f.id] = { actif: true, prixVente: conseille };
      // Stock initial ≈ 12 jours de demande de base (acheté comptant au prix d'achat plein).
      const demande = demandeBaseJour(f, secteur, positionnement, modele, 1);
      const qty = Math.max(10, Math.round(demande * 12));
      stock[f.id] = { qty, cump: f.prix_achat };
      stockValue += qty * f.prix_achat;
      ventes7[f.id] = [];
    });

    const droits = mod.droits_entree || 0;
    // SCD R1 — Tunnel d'ouverture : le 1er magasin vient du tunnel (type,
    // positionnement, localisation). Fallback : ancien point de vente fixe.
    const magPrincipal = magasinConfig
      ? buildMagasinFromTunnel({ ...magasinConfig, ouvertJour: 0 })
      : {
          nomType: pdv.nom_type,
          surface: pdv.surface_m2,
          loyerMensuel: pdv.loyer_mensuel,
          chargesMensuelles: pdv.charges_mensuelles,
          capaciteReserve: pdv.capacite_reserve,
          zoneId: pdv.zone_chalandise_id,
          coefTrafic: 1,
          coefPanier: 1,
        };
    const coutOuverture = magasinConfig
      ? (magasinConfig.devis?.coutOuverture || 0) - (magasinConfig.devis?.depotGarantie || 0)
      : 0;
    // SCD Lot 2 — Bail commercial : dépôt de garantie 3 mois de loyer.
    const depotGarantie = magPrincipal.loyerMensuel * 3;
    // SCD Lot 2 — Caviste : licence de vente d'alcool (coût fixe).
    const licenceAlcool = sec.licence_alcool_cout || 0;
    const decaissementInitial = Math.round(droits + stockValue + depotGarantie + licenceAlcool + coutOuverture);

    set(s => ({
      retail: {
        actif: true,
        secteur,
        positionnement,
        modele,
        // 30/06 — sous-options figées par secteur : le 1er magasin d'un
        // secteur fige (positionnement + localisation) pour les suivants.
        secteurConfigs: {
          [secteur]: {
            positionnement,
            localisationId: magasinConfig?.localisationId || null,
          },
        },
        magasin: magPrincipal,
        assortiment,
        stock,
        ventes7,
        commandes: [],
        // 30/06 — relation par fournisseur (0-100, départ 50).
        relationsFournisseurs: {},
        // 02/07 — consignes de réapprovisionnement automatique par famille.
        reapro: {},
        // 03/07 — merchandising interactif : planogramme + parcours client.
        merchandising: {},
        // SCD Lot 2 — Équipe magasin (RH retail).
        equipe: {
          employes: [],
          options: { dimanche: false, nocturnes: false, primes: false },
        },
        // SCD Lot 3 — Marketing : leviers, satisfaction, avis, promos, événements.
        marketing: defaultMarketing(),
        // SCD Lot 4 — Zones du magasin (niveaux), réseau de magasins, e-commerce.
        magasinZones: defaultMagasinZones(),
        magasins: [],
        ecommerce: defaultEcommerce(),
        // SCD Lot 2 — Fiscalité retail : TVA multi-taux (affichage pédagogique,
        // flux neutralisé : le client paie TTC, la TVA nette est reversée),
        // accises, TASCOM, dépôt de garantie.
        fiscal: {
          depotGarantie,
          licenceAlcool,
          tvaMoisCollectee: {},
          tvaMoisDeductible: 0,
          tvaDernierReversement: 0,
          tvaVerseeCumul: 0,
          accisesCumul: 0,
          tascomCumul: 0,
          fraisCbCumul: 0,
          redevancePubCumul: 0,
          masseSalarialeCumul: 0,
          dernierMoisTva: null,
        },
        stats: {
          caJour: 0, margeJour: 0, ventesJour: 0, demandeJour: 0, rupturesJourUnites: 0,
          caCumul: 0, margeCumul: 0,
          ruptureUnitesCumul: 0, ruptureCaManqueCumul: 0,
          demarqueEurosCumul: 0, demarqueUnitesCumul: 0,
          redevanceCumul: 0,
          histo: [],
        },
      },
      treasury: (s.treasury || 0) - decaissementInitial,
      notoriety: Math.min(100, (s.notoriety || 0) + (mod.notoriete_bonus || 0)),
    }));

    // SCD R3+ — financement par emprunt choisi au tunnel du 1er magasin :
    // la banque couvre travaux + caution (crédit 48 mois), le cash reste
    // disponible pour l'exploitation (le stock et les droits restent comptants).
    if (magasinConfig?.financement === 'emprunt' && typeof get().takeLoan === 'function') {
      get().takeLoan(Math.round(coutOuverture + depotGarantie), 48);
    }

    if (typeof get().pushCustomMessage === 'function') {
      get().pushCustomMessage({
        senderType: 'system',
        senderName: 'SCD — Ouverture du magasin',
        tone: 'positive',
        kind: 'info',
        subject: `Votre ${pdv.nom_type.toLowerCase()} est ouvert !`,
        body:
          `Félicitations, votre point de vente **${sec.nom}** (${getPositionnement(positionnement).nom}, ${mod.nom.toLowerCase()}) est prêt.\n\n` +
          `🏬 **Votre magasin** : ${pdv.surface_m2} m² — loyer ${pdv.loyer_mensuel.toLocaleString('fr-FR')} €/mois — réserve de ${pdv.capacite_reserve.toLocaleString('fr-FR')} unités.\n` +
          `📦 **Stock initial** : ${Math.round(stockValue).toLocaleString('fr-FR')} € d'achats comptants (≈ 12 jours de ventes).\n` +
          (droits > 0 ? `🏪 **Droits d'entrée franchise** : ${droits.toLocaleString('fr-FR')} € débités.\n` : '') +
          `\nÀ vous de jouer : ajustez vos prix dans **Assortiment & prix**, passez vos commandes dans **Achats & stock**, et suivez CA, marge et ruptures sur le tableau de bord.`,
        ttlDays: 30,
      });
    }
  },

  /** Modifie le prix de vente d'une famille (clampé : 50 % du CUMP → 4× le conseillé). */
  setPrixVente: (famId, prix) => {
    const r = get().retail;
    if (!r || !r.assortiment[famId]) return { ok: false };
    const sec = getSecteur(r.secteur);
    const fam = sec.familles.find(f => f.id === famId);
    const conseille = prixConseille(fam, r.positionnement);
    const cump = r.stock[famId]?.cump || fam.prix_achat;
    const clamped = Math.min(conseille * 4, Math.max(Math.round(cump * 0.5 * 100) / 100, prix));
    set({
      retail: {
        ...r,
        assortiment: { ...r.assortiment, [famId]: { ...r.assortiment[famId], prixVente: clamped } },
      },
    });
    return { ok: true, prix: clamped };
  },

  /** Active/désactive une famille de l'assortiment. */
  toggleFamille: (famId) => {
    const r = get().retail;
    if (!r || !r.assortiment[famId]) return;
    set({
      retail: {
        ...r,
        assortiment: {
          ...r.assortiment,
          [famId]: { ...r.assortiment[famId], actif: !r.assortiment[famId].actif },
        },
      },
    });
  },

  /**
   * Passe une commande fournisseur. Pas de décaissement immédiat :
   * paiement à 30/60 j après livraison selon le fournisseur.
   */
  passerCommande: ({ fournisseur, famId, qty }) => {
    const s = get();
    const r = s.retail;
    if (!r) return { ok: false, reason: 'no_retail' };
    const sec = getSecteur(r.secteur);
    const fam = sec.familles.find(f => f.id === famId);
    if (!fam || !fournisseur || !Number.isFinite(qty) || qty <= 0) {
      return { ok: false, reason: 'invalid' };
    }
    if (!fournisseurCouvre(fournisseur, famId)) {
      return { ok: false, reason: 'gamme' };
    }
    // Capacité réserve : stock actuel + commandes non reçues + cette commande.
    // SCD R3 — la réserve agrandie augmente la capacité en %.
    const capaciteReserve = Math.round(r.magasin.capaciteReserve * (1 + computeAmelFx(r.magasin).capaciteReservePct / 100));
    const stockTotal = Object.values(r.stock).reduce((a, x) => a + x.qty, 0);
    const enCours = r.commandes.filter(c => !c.recu).reduce((a, c) => a + c.qty, 0);
    if (stockTotal + enCours + qty > capaciteReserve) {
      return { ok: false, reason: 'capacite', restant: Math.max(0, capaciteReserve - stockTotal - enCours) };
    }
    const totalDays = s.totalDays || 0;
    const relation = getRelationFournisseur(r, fournisseur.id);
    const delai = Math.max(1, (fournisseur.delai_livraison_jours || 3) - relationNiveau(relation).delaiBonus);
    const remisePct = remisePourQty(fournisseur, qty, relation);
    // 30/06 — remise groupe : acheteurs support + centrale d'achats.
    const remiseGroupe = Math.min(0.12,
      (typeof get().getSupportAchatDiscount === 'function' ? get().getSupportAchatDiscount() : 0)
      + ((s.activeInvestments || {})['grp-centrale'] ? 0.03 : 0));
    const pu = Math.round(prixUnitaireNet(fam, fournisseur, qty, r.modele, relation) * (1 - remiseGroupe) * 100) / 100;
    const montant = Math.round(pu * qty * 100) / 100;
    const commande = {
      id: genId(),
      fournisseurId: fournisseur.id,
      fournisseurNom: fournisseur.nom,
      famId,
      famNom: fam.nom,
      qty,
      prixUnitaireNet: pu,
      remisePct,
      montant,
      grosseCommande: qty >= grosseCommandeSeuil(fournisseur),
      jourCommande: totalDays,
      jourLivraison: totalDays + delai,
      jourEcheance: totalDays + delai + (fournisseur.paiement_jours || 30),
      paiementJours: fournisseur.paiement_jours || 30,
      recu: false,
      paye: false,
    };
    set({ retail: { ...r, commandes: [...r.commandes, commande] } });
    return { ok: true, commande };
  },

  /* ════════ 02/07 — Consignes de réapprovisionnement automatique ════════ */

  /** Crée/édite la consigne d'une famille : { seuil, qty, prixMax|null }. */
  setConsigneReapro: ({ famId, seuil, qty, prixMax = null }) => {
    const r = get().retail;
    if (!r || !famId || !Number.isFinite(seuil) || !Number.isFinite(qty) || seuil < 0 || qty <= 0) {
      return { ok: false };
    }
    set({ retail: { ...r, reapro: { ...(r.reapro || {}), [famId]: { seuil: Math.round(seuil), qty: Math.round(qty), prixMax: prixMax != null && Number.isFinite(prixMax) && prixMax > 0 ? prixMax : null, alerted: false } } } });
    return { ok: true };
  },

  supprimerConsigneReapro: (famId) => {
    const r = get().retail;
    if (!r?.reapro?.[famId]) return;
    const reapro = { ...r.reapro };
    delete reapro[famId];
    set({ retail: { ...r, reapro } });
  },

  /** 03/07 — Planogramme par secteur : première mise en place offerte, puis manutention. */
  appliquerFacing: (slots, secteurId = null) => {
    const s = get();
    const r = s.retail;
    if (!r || !slots) return { ok: false };
    const sec = secteurId || r.secteur;
    const cfg = merchSecteur(r, sec);
    const cout = cfg.facing ? coutFacing(typeMagasinSecteur(r, sec)) : 0;
    if ((s.treasury || 0) < cout) return { ok: false, reason: 'fonds' };
    const slotsFinal = forcerVerrous(r, sec, slots);
    const fx = computeFacing(r, slotsFinal, { secteurId: sec, month: s.month || 1 });
    const merch = migrerMerch(r);
    set({
      treasury: (s.treasury || 0) - cout,
      retail: { ...r, merchandising: { ...merch, [sec]: { ...cfg, facing: { slots: slotsFinal, jour: s.totalDays || 0, score: fx.score } } } },
    });
    return { ok: true, cout, score: fx.score };
  },

  /** 03/07 — Parcours client par secteur : première mise en place offerte, puis réagencement. */
  appliquerParcours: (cells, secteurId = null) => {
    const s = get();
    const r = s.retail;
    if (!r || !Array.isArray(cells)) return { ok: false };
    const sec = secteurId || r.secteur;
    const cfg = merchSecteur(r, sec);
    const cout = cfg.parcours ? coutParcours(typeMagasinSecteur(r, sec)) : 0;
    if ((s.treasury || 0) < cout) return { ok: false, reason: 'fonds' };
    const fx = computeParcours(r, cells, { secteurId: sec, month: s.month || 1 });
    const merch = migrerMerch(r);
    set({
      treasury: (s.treasury || 0) - cout,
      retail: { ...r, merchandising: { ...merch, [sec]: { ...cfg, parcours: { cells, jour: s.totalDays || 0, score: fx.score } } } },
    });
    return { ok: true, cout, score: fx.score };
  },

  /** 05/07 — Accepte une tête de gondole : facings verrouillés, relation +5, coopération encaissée chaque jour. */
  accepterOffreFacing: (offreId) => {
    const s = get();
    const r = s.retail;
    const etat = etatFacingNegocie(r);
    const offre = etat.offres.find(o => o.id === offreId);
    if (!offre) return { ok: false };
    const jour = s.totalDays || 0;
    const contrat = { ...offre, debutJour: jour, finJour: jour + offre.dureeJours };
    const cfg = merchSecteur(r, offre.secteurId);
    const base = cfg.facing?.slots || suggererFacing(r, { secteurId: offre.secteurId, month: s.month || 1 });
    const slots = forcerVerrous({ ...r, facingNegocie: { ...etat, contrats: [...etat.contrats, contrat] } }, offre.secteurId, base);
    const fx = computeFacing(r, slots, { secteurId: offre.secteurId, month: s.month || 1 });
    const relations = { ...(r.relationsFournisseurs || {}) };
    relations[offre.fournisseurId] = Math.min(100, (relations[offre.fournisseurId] ?? RELATION_DEFAUT) + BONUS_RELATION_ACCEPT);
    set({
      retail: {
        ...r,
        relationsFournisseurs: relations,
        merchandising: { ...migrerMerch(r), [offre.secteurId]: { ...cfg, facing: { slots, jour, score: fx.score } } },
        facingNegocie: { ...etat, offres: etat.offres.filter(o => o.id !== offreId), contrats: [...etat.contrats, contrat] },
      },
    });
    return { ok: true, score: fx.score, contrat };
  },

  refuserOffreFacing: (offreId) => {
    const r = get().retail;
    const etat = etatFacingNegocie(r);
    if (!etat.offres.some(o => o.id === offreId)) return { ok: false };
    set({ retail: { ...r, facingNegocie: { ...etat, offres: etat.offres.filter(o => o.id !== offreId) } } });
    return { ok: true };
  },

  /** Centrale d'achats active : configure toutes les familles en un clic
   *  (seuil = 10 jours de ventes, quantité = 30 jours, sans prix max). */
  pilotageCentrale: () => {
    const s = get();
    const r = s.retail;
    if (!r || !(s.activeInvestments || {})['grp-centrale']) return { ok: false, reason: 'centrale_requise' };
    const sec = getSecteur(r.secteur);
    const reapro = { ...(r.reapro || {}) };
    sec.familles.forEach((f) => {
      const hist = r.ventes7?.[f.id] || [];
      const vj = hist.length ? hist.reduce((a, b) => a + b, 0) / hist.length : 0;
      reapro[f.id] = {
        seuil: Math.max(5, Math.round(vj * 10)),
        qty: Math.max(10, Math.round(vj * 30)),
        prixMax: null,
        alerted: false,
        centrale: true,
      };
    });
    set({ retail: { ...r, reapro } });
    return { ok: true, nb: sec.familles.length };
  },

  /* ════════════════ SCD Lot 2 — Actions RH retail ════════════════ */

  /** Embauche un employé (contrat: 'cdi' | 'alternance' | 'cdd_saisonnier'). */
  embaucherEmploye: ({ metierId, contrat = 'cdi' }) => {
    const s = get();
    const r = s.retail;
    if (!r) return { ok: false, reason: 'no_retail' };
    const totalDays = s.totalDays || 0;
    const employes = r.equipe?.employes || [];
    let salaire; let nom; let finJour = null;
    if (contrat === 'alternance') {
      const deja = employes.filter(e => e.contrat === 'alternance' && (!e.finJour || e.finJour > totalDays)).length;
      if (deja >= (CONTRATS_SPECIAUX.alternant.max || 2)) return { ok: false, reason: 'max_alternants' };
      salaire = CONTRATS_SPECIAUX.alternant.salaire;
      metierId = 'vendeur';
      nom = randomNom();
    } else if (contrat === 'cdd_saisonnier') {
      salaire = CONTRATS_SPECIAUX.renfort_saisonnier.salaire;
      finJour = totalDays + (CONTRATS_SPECIAUX.renfort_saisonnier.duree_jours || 30);
      metierId = 'vendeur';
      nom = randomNom();
    } else {
      const metier = getMetier(metierId);
      if (!metier) return { ok: false, reason: 'metier_inconnu' };
      if (metier.secteur && metier.secteur !== r.secteur) return { ok: false, reason: 'secteur' };
      const deja = employes.filter(e => e.metierId === metierId && e.contrat === 'cdi' && (!e.finJour || e.finJour > totalDays)).length;
      if (deja >= (metier.max || 9)) return { ok: false, reason: 'max_poste' };
      salaire = metier.salaire;
      nom = randomNom();
    }
    const emp = {
      id: `emp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      nom, metierId, contrat, salaire,
      embaucheJour: totalDays, finJour,
      forme: false, enFormationJusqua: null,
    };
    set({ retail: { ...r, equipe: { ...r.equipe, employes: [...employes, emp] } } });
    return { ok: true, employe: emp };
  },

  /** Licencie un employé (indemnité = 1 mois de salaire chargé). */
  licencierEmploye: (empId) => {
    const s = get();
    const r = s.retail;
    if (!r) return { ok: false };
    const emp = (r.equipe?.employes || []).find(e => e.id === empId);
    if (!emp) return { ok: false };
    const indemnite = Math.round(emp.salaire * 1.42);
    set({
      retail: { ...r, equipe: { ...r.equipe, employes: r.equipe.employes.filter(e => e.id !== empId) } },
      treasury: (s.treasury || 0) - indemnite,
    });
    return { ok: true, indemnite };
  },

  /** Envoie un employé en formation (coût fixe, absent 7 j, revient +25 % efficace). */
  formerEmploye: (empId) => {
    const s = get();
    const r = s.retail;
    if (!r) return { ok: false };
    const emp = (r.equipe?.employes || []).find(e => e.id === empId);
    if (!emp || emp.forme || (emp.enFormationJusqua && emp.enFormationJusqua > (s.totalDays || 0))) return { ok: false, reason: 'deja' };
    if ((s.treasury || 0) < FORMATION.cout) return { ok: false, reason: 'fonds' };
    set({
      retail: {
        ...r,
        equipe: {
          ...r.equipe,
          employes: r.equipe.employes.map(e => e.id === empId
            ? { ...e, forme: true, enFormationJusqua: (s.totalDays || 0) + (FORMATION.duree_jours || 7) }
            : e),
        },
      },
      treasury: (s.treasury || 0) - FORMATION.cout,
    });
    return { ok: true };
  },

  /** Active/désactive une option de planning (dimanche, nocturnes, primes). */
  toggleOptionPlanning: (key) => {
    const r = get().retail;
    if (!r || !['dimanche', 'nocturnes', 'primes'].includes(key)) return;
    set({
      retail: {
        ...r,
        equipe: { ...r.equipe, options: { ...r.equipe.options, [key]: !r.equipe.options[key] } },
      },
    });
  },

  /* ════════════════ SCD Lot 3 — Actions marketing ════════════════ */

  /** Active/désactive un levier marketing (coût d'installation débité la 1re fois). */
  toggleLevierMarketing: (levierId) => {
    const s = get();
    const r = s.retail;
    const levier = getLevier(levierId);
    if (!r || !levier) return { ok: false };
    const mkt = r.marketing || defaultMarketing();
    const activation = !mkt.leviers?.[levierId];
    let treasury = s.treasury || 0;
    let installes = mkt.installes || {};
    let installe = false;
    if (activation && levier.cout_installation && !installes[levierId]) {
      if (treasury < levier.cout_installation) return { ok: false, reason: 'fonds' };
      treasury -= levier.cout_installation;
      installes = { ...installes, [levierId]: true };
      installe = true;
    }
    set({
      retail: { ...r, marketing: { ...mkt, leviers: { ...mkt.leviers, [levierId]: activation }, installes } },
      treasury,
    });
    return { ok: true, actif: activation, installe };
  },

  /** Lance une promotion générale (−pct % sur tout l'assortiment, dureeJours). */
  lancerPromo: ({ pct, dureeJours }) => {
    const s = get();
    const r = s.retail;
    if (!r) return { ok: false };
    const mkt = r.marketing || defaultMarketing();
    const totalDays = s.totalDays || 0;
    if (mkt.promo && mkt.promo.until > totalDays) return { ok: false, reason: 'deja_active' };
    if (!PROMO_CONFIG.paliers_pct.includes(pct) || !PROMO_CONFIG.durees_jours.includes(dureeJours)) return { ok: false, reason: 'invalid' };
    set({
      retail: { ...r, marketing: { ...mkt, promo: { pct, debut: totalDays, until: totalDays + dureeJours } } },
    });
    return { ok: true };
  },

  /** Arrête la promotion en cours. */
  arreterPromo: () => {
    const r = get().retail;
    if (!r?.marketing?.promo) return;
    set({ retail: { ...r, marketing: { ...r.marketing, promo: null } } });
  },

  /* ════════════════ SCD Lot 4 — Magasin, réseau, e-commerce ════════════════ */

  /** SCD R3 — Lance les travaux d'une amélioration sur un magasin ('principal' ou id réseau). */
  acheterAmelioration: ({ magasinId = 'principal', amelioId }) => {
    const s = get();
    const r = s.retail;
    if (!r) return { ok: false };
    const totalDays = s.totalDays || 0;
    const mag = magasinId === 'principal' ? r.magasin : (r.magasins || []).find(m => m.id === magasinId);
    if (!mag) return { ok: false, reason: 'magasin' };
    const amelio = getAmelioration(amelioId);
    const check = peutAcheter({ amelio, magasin: mag, secteurId: r.secteur, treasury: s.treasury || 0, totalDays });
    if (!check.ok) return check;
    const chantier = {
      amelioId,
      niveauCible: check.niveau.niveau,
      debutJour: totalDays,
      finJour: totalDays + Math.max(1, check.niveau.travaux_jours || 1),
      fermeturePartielle: !!check.niveau.fermeture_partielle,
    };
    const maj = { ...mag, chantiers: [...(mag.chantiers || []), chantier] };
    // Journal des investissements (Historique & ROI + supervision formateur).
    const journalInvest = [...(r.journalInvest || []), {
      jour: totalDays,
      jourEffet: chantier.finJour,
      magasinId,
      magNom: magasinId === 'principal' ? (mag.nom || mag.nomType || 'Magasin principal') : (mag.nom || mag.nomType || 'Magasin'),
      amelioId,
      niveau: check.niveau.niveau,
      nom: `${amelio.nom}${check.niveau.niveau > 1 ? ` niv. ${check.niveau.niveau}` : ''}`,
      cout: check.cout,
    }].slice(-60);
    set({
      treasury: (s.treasury || 0) - check.cout,
      retail: magasinId === 'principal'
        ? { ...r, magasin: maj, journalInvest }
        : { ...r, magasins: r.magasins.map(m => (m.id === magasinId ? maj : m)), journalInvest },
    });
    return { ok: true, cout: check.cout, chantier, niveau: check.niveau };
  },

  /** ── Gestion du parc ─────────────────────────────────────────── */
  /** Achète les murs d'un magasin (cash ou hypothèque bancaire 15 ans). */
  acheterMurs: (magasinId, financement = 'tresorerie') => {
    const s = get();
    const r = s.retail;
    const mag = magasinId === 'principal' ? r?.magasin : (r?.magasins || []).find(m => m.id === magasinId);
    if (!mag || mag.murs) return { ok: false };
    const prix = prixMurs(mag);
    if (financement === 'tresorerie' && (s.treasury || 0) < prix) return { ok: false, reason: 'fonds' };
    if (financement === 'hypotheque') {
      if (typeof get().takeLoan !== 'function') return { ok: false, reason: 'banque' };
      get().takeLoan(prix, DUREE_HYPOTHEQUE_MOIS, TAUX_HYPOTHEQUE);
    }
    const maj = { ...mag, murs: { prix, achatJour: s.totalDays || 0, financement } };
    set({
      treasury: (get().treasury || 0) - prix,
      retail: magasinId === 'principal'
        ? { ...get().retail, magasin: maj }
        : { ...get().retail, magasins: get().retail.magasins.map(m => (m.id === magasinId ? maj : m)) },
    });
    return { ok: true, prix };
  },

  /** Met un magasin du réseau en vente (fonds seul ou murs inclus). */
  mettreEnVente: (magasinId, prix, vendreMurs = false) => {
    const s = get();
    const r = s.retail;
    const mag = (r?.magasins || []).find(m => m.id === magasinId);
    if (!mag || mag.vente) return { ok: false };
    const vente = { statut: 'en_vente', prix: Math.max(1000, Math.round(prix)), depuisJour: s.totalDays || 0, offre: null, vendreMurs: !!(vendreMurs && mag.murs) };
    set({ retail: { ...r, magasins: r.magasins.map(m => (m.id === magasinId ? { ...m, vente } : m)) } });
    return { ok: true };
  },

  annulerVente: (magasinId) => {
    const r = get().retail;
    set({ retail: { ...r, magasins: r.magasins.map(m => (m.id === magasinId ? { ...m, vente: null } : m)) } });
    return { ok: true };
  },

  /** Répond à une offre : 'accepter' | 'contre' | 'refuser'. */
  repondreOffre: (magasinId, action) => {
    const s = get();
    const r = s.retail;
    const mag = (r?.magasins || []).find(m => m.id === magasinId);
    const offre = mag?.vente?.offre;
    if (!mag || !offre) return { ok: false };
    if (action === 'refuser') {
      set({ retail: { ...r, magasins: r.magasins.map(m => (m.id === magasinId ? { ...m, vente: { ...m.vente, offre: null } } : m)) } });
      return { ok: true, statut: 'refusee' };
    }
    if (action === 'contre') {
      if (offre.contre) return { ok: false, reason: 'deja_contre' };
      const cible = Math.round((offre.montant + mag.vente.prix) / 2);
      if (Math.random() < 0.55) {
        set({ retail: { ...r, magasins: r.magasins.map(m => (m.id === magasinId ? { ...m, vente: { ...m.vente, offre: { ...offre, montant: cible, contre: true, expireJour: (s.totalDays || 0) + 3 } } } : m)) } });
        return { ok: true, statut: 'contre_acceptee', montant: cible };
      }
      set({ retail: { ...r, magasins: r.magasins.map(m => (m.id === magasinId ? { ...m, vente: { ...m.vente, offre: null } } : m)) } });
      return { ok: true, statut: 'retrait' };
    }
    // Accepter : encaisse le fonds (+ murs si vendus ensemble), sinon les murs deviennent un bien locatif.
    let gain = offre.montant;
    let biens = r.biensLocatifs || [];
    if (mag.murs) {
      if (mag.vente.vendreMurs) gain += Math.round(valeurMurs(mag, s.totalDays || 0) * 0.97);
      else biens = [...biens, { id: `bien-${Date.now().toString(36)}`, villeId: villeDuMagasin(mag), nom: mag.nom, loyerMensuel: mag.loyerMensuel, valeurMurs: valeurMurs(mag, s.totalDays || 0), achatJour: mag.murs.achatJour }];
    }
    set({
      treasury: (s.treasury || 0) + gain,
      retail: { ...r, magasins: r.magasins.filter(m => m.id !== magasinId), biensLocatifs: biens },
    });
    return { ok: true, statut: 'vendu', montant: gain, mursConserves: !!(mag.murs && !mag.vente.vendreMurs) };
  },

  /** Liquidation : 7 jours de vente spéciale puis fermeture sans indemnité. */
  lancerLiquidation: (magasinId) => {
    const s = get();
    const r = s.retail;
    const mag = (r?.magasins || []).find(m => m.id === magasinId);
    if (!mag) return { ok: false };
    const vente = { statut: 'liquidation', prix: 0, depuisJour: s.totalDays || 0, finJour: (s.totalDays || 0) + LIQUIDATION_JOURS, offre: null };
    let retail = { ...r, magasins: r.magasins.map(m => (m.id === magasinId ? { ...m, vente } : m)) };
    retail = applyRetailEventEffect(retail, { traficMult: 1.12, surcoutPctCa: 1.1, duree_jours: LIQUIDATION_JOURS }, s.totalDays || 0, `Liquidation — ${mag.nom}`);
    set({ retail });
    return { ok: true };
  },

  /** Fermeture sèche : immédiate, indemnité de bail 3 mois (sauf si murs possédés). */
  fermerMagasin: (magasinId) => {
    const s = get();
    const r = s.retail;
    const mag = (r?.magasins || []).find(m => m.id === magasinId);
    if (!mag) return { ok: false };
    const indemnite = mag.murs ? 0 : mag.loyerMensuel * INDEMNITE_BAIL_MOIS;
    let biens = r.biensLocatifs || [];
    if (mag.murs) biens = [...biens, { id: `bien-${Date.now().toString(36)}`, villeId: villeDuMagasin(mag), nom: mag.nom, loyerMensuel: mag.loyerMensuel, valeurMurs: valeurMurs(mag, s.totalDays || 0), achatJour: mag.murs.achatJour }];
    set({
      treasury: (s.treasury || 0) - indemnite,
      retail: { ...r, magasins: r.magasins.filter(m => m.id !== magasinId), biensLocatifs: biens },
    });
    return { ok: true, indemnite };
  },

  /** Vend un bien locatif (les murs conservés d'un ancien magasin). */
  vendreBienLocatif: (bienId) => {
    const s = get();
    const r = s.retail;
    const bien = (r?.biensLocatifs || []).find(b => b.id === bienId);
    if (!bien) return { ok: false };
    const gain = Math.round((bien.valeurMurs || 0) * 0.97);
    set({ treasury: (s.treasury || 0) + gain, retail: { ...r, biensLocatifs: r.biensLocatifs.filter(b => b.id !== bienId) } });
    return { ok: true, gain };
  },

  /** Résout une interaction concurrente (guerre des prix ou rachat opportuniste). */
  resoudreConcurrent: (choix) => {
    const s = get();
    const r = s.retail;
    const ev = r?.concurrentEvent;
    if (!ev) return { ok: false };
    let retail = { ...r, concurrentEvent: null };
    let treasury = s.treasury || 0;
    const jour = s.totalDays || 0;
    if (ev.type === 'guerre') {
      if (choix === 'suivre') retail = applyRetailEventEffect(retail, { surcoutPctCa: 1.5, duree_jours: 10 }, jour, 'Guerre des prix — alignement');
      else if (choix === 'campagne') {
        if (treasury < 600) return { ok: false, reason: 'fonds' };
        treasury -= 600;
        retail = applyRetailEventEffect(retail, { traficMult: 1.04, duree_jours: 10 }, jour, 'Campagne locale anti-concurrence');
      } else {
        retail = { ...retail, concurrents: { ...retail.concurrents, implantations: retail.concurrents.implantations.map(i => (i.villeId === ev.villeId ? { ...i, guerreFinJour: jour + 10 } : i)) } };
      }
    } else if (ev.type === 'rachat') {
      if (choix === 'racheter' || choix === 'negocier') {
        const prix = choix === 'negocier' ? Math.round(ev.prix * 0.7) : ev.prix;
        if (choix === 'negocier' && Math.random() >= 0.5) return { ok: true, statut: 'negociation_echouee' };
        if (treasury < prix) return { ok: false, reason: 'fonds' };
        treasury -= prix;
        const ville = VILLES.find(v => v.id === ev.villeId);
        retail = {
          ...retail,
          concurrents: { ...retail.concurrents, implantations: retail.concurrents.implantations.filter(i => i.villeId !== ev.villeId) },
          magasins: [...(retail.magasins || []), {
            id: `mag-${Date.now().toString(36)}`, nom: `Boutique — ${ville?.nom || ev.villeId} (reprise)`, typeId: 'boutique', secteurId: retail.secteur,
            localisationId: 'centre_ville', villeId: ev.villeId, positionnement: retail.positionnement || 'coeur',
            surface: 90, loyerMensuel: Math.round(900 * (ville?.coef_loyer || 1)), chargesMensuelles: Math.round(900 * (ville?.coef_loyer || 1) * 0.22),
            coefTrafic: 1.26 * (ville?.bassin || 1), coefPanier: 1.05, concurrenceMalus: 1, ouvertJour: jour, capaciteReserve: 400,
          }],
        };
        set({ treasury, retail });
        return { ok: true, statut: 'rachete', prix };
      }
    }
    set({ treasury, retail });
    return { ok: true, statut: choix };
  },

  /** Résout une mécanique retail pédagogique (coût/gain + effets temporaires). */
  resoudreMecanique: (choixIdx) => {
    const s = get();
    const r = s.retail;
    const active = r?.mecaniqueActive;
    if (!active) return { ok: false };
    const mecanique = getMecanique(active.id);
    const choix = mecanique?.choix?.[choixIdx];
    if (!choix) return { ok: false };
    let retail = r;
    if (choix.effets) {
      retail = applyRetailEventEffect(retail, choix.effets, s.totalDays || 0, mecanique.concept);
    }
    const meca = retail.mecaniques || { vues: {}, prochainJour: 0 };
    retail = {
      ...retail,
      mecaniqueActive: null,
      mecaniques: {
        vues: { ...meca.vues, [active.id]: { jour: s.totalDays || 0, choix: choixIdx } },
        prochainJour: (s.totalDays || 0) + delaiProchaineMecanique(),
      },
    };
    set({ retail, treasury: (s.treasury || 0) - (choix.cout || 0) + (choix.gain || 0) });
    return { ok: true, mecanique, choix };
  },

  /** SCD R1 — Ouvre un magasin via le tunnel (trésorerie ou emprunt 48 mois). */
  ouvrirMagasinTunnel: (config) => {
    const s = get();
    const r = s.retail;
    if (!r || !config?.devis) return { ok: false };
    if (config.villeId) {
      // Max 1 magasin de chaque type par ville (statuts Présence → Domination).
      if (typesVille(r, config.villeId).includes(config.typeId)) return { ok: false, reason: 'type_ville' };
    }
    const cout = config.devis.coutOuverture;
    if (config.financement === 'emprunt') {
      if (typeof get().takeLoan === 'function') {
        // L'emprunt crédite la trésorerie, puis le coût d'ouverture est payé :
        // effet net nul sur le cash, la dette porte le financement.
        get().takeLoan(cout, 48);
        set({ treasury: (get().treasury || 0) - cout });
      } else return { ok: false, reason: 'emprunt_indisponible' };
    } else {
      if ((s.treasury || 0) < cout) return { ok: false, reason: 'fonds' };
      set({ treasury: (s.treasury || 0) - cout });
    }
    const mag = buildMagasinFromTunnel({ ...config, ouvertJour: s.totalDays || 0 });
    // 30/06 — enregistre la config figée du secteur (1re ouverture de ce secteur).
    const secKey = config.secteurId || r.secteur;
    const configs = r.secteurConfigs || { [r.secteur]: { positionnement: r.positionnement, localisationId: r.magasin?.localisationId || null } };
    const secteurConfigs = configs[secKey]
      ? configs
      : { ...configs, [secKey]: { positionnement: config.positionnement, localisationId: config.localisationId } };
    set({ retail: { ...get().retail, secteurConfigs, magasins: [...(get().retail.magasins || []), mag] } });
    // 04/07 — nouveau secteur sans planogramme : −3 % de ventes jusqu'à l'organisation du rayon.
    if (secKey !== r.secteur && !merchSecteur(get().retail, secKey).facing) {
      const secNom = getSecteur(secKey)?.nom || secKey;
      get().pushCustomMessage?.({ senderType: 'system', senderName: 'SCD — Merchandising', tone: 'warning', kind: 'info', subject: `🧭 Nouveau secteur à organiser : ${secNom}`, body: `Votre nouveau magasin ${mag.nom} ouvre un secteur d'activité (${secNom}) dont le rayon et le parcours client ne sont pas encore conçus. Tant que le planogramme n'est pas posé (page Merchandising, onglet ${secNom}), les magasins de ce secteur subissent −3 % de ventes. Les autres magasins du même secteur suivront automatiquement la conception.`, ttlDays: 30 });
    }
    get().checkGroupeUnlock?.();
    return { ok: true, magasin: mag, cout };
  },

  /** Ouvre un magasin secondaire (périphérie / corner). 1 max par type. */
  ouvrirMagasin: (typeId) => {
    const s = get();
    const r = s.retail;
    const type = getTypeExpansion(typeId);
    if (!r || !type) return { ok: false };
    if ((r.magasins || []).some(m => m.typeId === typeId)) return { ok: false, reason: 'deja' };
    const cout = type.travaux + type.loyer_mensuel * 3;
    if ((s.treasury || 0) < cout) return { ok: false, reason: 'fonds', cout };
    const mag = {
      id: `mag-${Date.now().toString(36)}`,
      typeId,
      nom: type.nom,
      zoneId: type.zone_chalandise_id,
      loyerMensuel: type.loyer_mensuel,
      chargesMensuelles: type.charges_mensuelles,
      ouvertJour: s.totalDays || 0,
    };
    set({
      retail: { ...r, magasins: [...(r.magasins || []), mag] },
      treasury: (s.treasury || 0) - cout,
    });
    get().checkGroupeUnlock?.();
    return { ok: true, magasin: mag, cout };
  },

  /** 30/06 — Dynamique groupe : à 3 magasins en propre, annonce (vidéo +
   *  message) le déblocage du siège social et des fonctions support. */
  checkGroupeUnlock: () => {
    const s = get();
    const r = s.retail;
    if (!r || r.groupeAnnonce) return;
    if (1 + (r.magasins || []).length < 3) return;
    set({ retail: { ...r, groupeAnnonce: true } });
    if (typeof get().triggerMilestoneVideo === 'function') {
      get().triggerMilestoneVideo({ kind: 'groupe-unlocked' });
    }
    if (typeof get().pushCustomMessage === 'function') {
      get().pushCustomMessage({
        senderType: 'system', senderName: 'SCD — Direction', tone: 'positive', kind: 'info',
        subject: '🏢 Dynamique groupe enclenchée !',
        body: "Avec 3 magasins en propre, votre enseigne devient un groupe. Investissez dans un **siège social** (onglet Groupe) pour recruter des fonctions support : direction de réseau, achats, marketing, RH, finance… Chaque recrue a un effet mesurable sur vos magasins.",
        ttlDays: 15,
      });
    }
  },

  /** Passe au palier e-commerce suivant (coût de lancement débité). */
  lancerPalierEcommerce: () => {
    const s = get();
    const r = s.retail;
    if (!r) return { ok: false };
    const ec = r.ecommerce || defaultEcommerce();
    const suivant = getPalier((ec.palier || 0) + 1);
    if (!suivant) return { ok: false, reason: 'max' };
    if ((s.treasury || 0) < suivant.cout_lancement) return { ok: false, reason: 'fonds' };
    set({
      retail: { ...r, ecommerce: { ...ec, palier: suivant.niveau } },
      treasury: (s.treasury || 0) - suivant.cout_lancement,
    });
    return { ok: true, palier: suivant };
  },

  /** Active/désactive un levier d'acquisition web (SEO/pub/marketplaces). */
  toggleAcquisitionEc: (id) => {
    const s = get();
    const r = s.retail;
    const levier = EC_ACQUISITION.find(a => a.id === id);
    if (!r || !levier) return { ok: false };
    const ec = r.ecommerce || defaultEcommerce();
    if ((levier.niveau_min || 0) > (ec.palier || 0)) return { ok: false, reason: 'palier' };
    const actif = !ec.acquisition?.[id];
    const patch = { acquisition: { ...ec.acquisition, [id]: actif } };
    if (id === 'seo' && actif) patch.seoDepuis = s.totalDays || 0;
    set({ retail: { ...r, ecommerce: { ...ec, ...patch } } });
    return { ok: true, actif };
  },

  /** Choisit l'option logistique d'expédition des commandes web. */
  setLogistiqueEc: (id) => {
    const r = get().retail;
    if (!r || !EC_LOGISTIQUE.some(l => l.id === id)) return;
    set({ retail: { ...r, ecommerce: { ...(r.ecommerce || defaultEcommerce()), logistique: id } } });
  },

  /* ── Franchise (30/06) : structuration, conditions, candidatures ── */

  /** Paie la structuration du réseau (25 000 €, comptant ou emprunt 36 mois). */
  creerReseauFranchise: (financement = 'tresorerie') => {
    const s = get();
    const r = s.retail;
    if (!r || r.franchise?.cree) return { ok: false, error: 'Réseau déjà structuré.' };
    if (!franchiseDebloquee(r, s.totalDays || 0).ok) return { ok: false, error: 'Conditions de déblocage non réunies.' };
    if (financement === 'tresorerie' && (s.treasury || 0) < COUT_STRUCTURATION_FRANCHISE) {
      return { ok: false, error: 'Trésorerie insuffisante.' };
    }
    if (financement === 'emprunt') {
      if (typeof get().takeLoan === 'function') get().takeLoan(COUT_STRUCTURATION_FRANCHISE, 36);
    }
    set({
      treasury: (get().treasury || 0) - COUT_STRUCTURATION_FRANCHISE,
      retail: {
        ...get().retail,
        franchise: {
          cree: true,
          creeJour: s.totalDays || 0,
          droitEntree: DROITS_ENTREE[1].montant,
          redevancePct: REDEVANCES_PCT[1].pct,
          candidature: null,
          prochainJour: (s.totalDays || 0) + delaiProchaineCandidature(DROITS_ENTREE[1].montant, REDEVANCES_PCT[1].pct),
          franchises: [],
          totalRedevances: 0,
          totalDroitsEntree: 0,
        },
      },
    });
    if (typeof get().pushCustomMessage === 'function') {
      get().pushCustomMessage({ senderType: 'system', senderName: 'SCD — Franchise', tone: 'positive', kind: 'info', subject: '🏗️ Votre réseau de franchise est structuré !', body: `Manuel opératoire, concept et cadre juridique sont prêts (${COUT_STRUCTURATION_FRANCHISE.toLocaleString('fr-FR')} €). Fixez vos conditions : plus elles sont douces, plus les candidatures affluent.`, ttlDays: 10 });
    }
    return { ok: true };
  },

  /** Ajuste droit d'entrée et redevance (s'applique aux prochaines candidatures). */
  setConditionsFranchise: ({ droitEntree, redevancePct }) => {
    const r = get().retail;
    if (!r?.franchise?.cree) return;
    set({ retail: { ...r, franchise: { ...r.franchise, droitEntree, redevancePct } } });
  },

  /** Accepte ou refuse la candidature en cours. */
  repondreCandidatureFranchise: (accepter) => {
    const s = get();
    const r = s.retail;
    const fr = r?.franchise;
    if (!fr?.candidature) return { ok: false };
    const cand = fr.candidature;
    const jour = s.totalDays || 0;
    const prochainJour = jour + delaiProchaineCandidature(fr.droitEntree, fr.redevancePct);
    if (!accepter) {
      set({ retail: { ...r, franchise: { ...fr, candidature: null, prochainJour } } });
      return { ok: true, accepte: false };
    }
    const nouveau = { id: cand.id, nom: cand.nom, villeId: cand.villeId, villeNom: cand.villeNom, signeJour: jour, resultatMensuel: cand.resultatMensuel, totalRedevances: 0 };
    set({
      treasury: (s.treasury || 0) + fr.droitEntree,
      retail: { ...r, franchise: { ...fr, candidature: null, prochainJour, franchises: [...(fr.franchises || []), nouveau], totalDroitsEntree: (fr.totalDroitsEntree || 0) + fr.droitEntree } },
    });
    if (typeof get().pushCustomMessage === 'function') {
      get().pushCustomMessage({ senderType: 'system', senderName: 'SCD — Franchise', tone: 'positive', kind: 'info', subject: `✍️ ${cand.nom} rejoint votre réseau !`, body: `Droit d'entrée encaissé : ${fr.droitEntree.toLocaleString('fr-FR')} €. Le magasin franchisé de ${cand.villeNom} versera ${fr.redevancePct} % de son résultat chaque mois. La ville compte désormais dans votre couverture réseau.`, ttlDays: 8 });
    }
    return { ok: true, accepte: true };
  },

  /** Répond à la demande d'aide d'un franchisé (animation commerciale). */
  resoudreAideFranchise: (choixId) => {
    const s = get();
    const r = s.retail;
    const fr = r?.franchise;
    const ev = fr?.aideEvent;
    if (!ev) return { ok: false };
    const choix = AIDE_FRANCHISE_CHOIX.find(c => c.id === choixId);
    if (!choix) return { ok: false };
    if (choix.cout > 0 && (s.treasury || 0) < choix.cout) return { ok: false, error: 'Trésorerie insuffisante.' };
    const franchises = (fr.franchises || []).map(f =>
      f.id === ev.franchiseId ? { ...f, resultatMensuel: Math.round(f.resultatMensuel * (1 + choix.effetPct / 100)) } : f
    );
    const stats = { ...(fr.aidesStats || { demandes: 0, financees: 0, refusees: 0 }) };
    stats.demandes += 1;
    if (choix.effetPct > 0) stats.financees += 1; else stats.refusees += 1;
    set({
      treasury: (s.treasury || 0) - choix.cout,
      retail: { ...r, franchise: { ...fr, aideEvent: null, franchises, aidesStats: stats } },
    });
    if (typeof get().pushCustomMessage === 'function') {
      get().pushCustomMessage({ senderType: 'system', senderName: 'SCD — Franchise', tone: choix.effetPct > 0 ? 'positive' : 'urgent', kind: 'info', subject: choix.effetPct > 0 ? `🎪 Animation lancée à ${ev.villeNom}` : `❌ Aide refusée à ${ev.villeNom}`, body: `${choix.detail} Résultat mensuel de ${ev.nom} : ${choix.effetPct > 0 ? '+' : ''}${choix.effetPct} %.`, ttlDays: 6 });
    }
    return { ok: true };
  },

  /**
   * Tick quotidien retail — appelé par coreSlice.nextDay AVANT l'application
   * du jour suivant. Retourne { ca, decaissements } (arrondis, HT).
   */
  tickRetail: (working) => {
    const s = get();
    const r = s.retail;
    if (!r || !r.actif) return null;
    const sec = getSecteur(r.secteur);
    if (!sec) return null;
    const mod = getModele(r.modele);
    const totalDays = s.totalDays || 0;
    const month = s.month || 1;

    const stock = {};
    Object.entries(r.stock).forEach(([k, v]) => { stock[k] = { ...v }; });
    const ventes7 = {};
    Object.entries(r.ventes7 || {}).forEach(([k, v]) => { ventes7[k] = [...v]; });

    // 1. Réceptions — retard/aléa possible (fiabilité × relation), puis livraison.
    //    Relation fournisseur : +2 par livraison (+1 grosse commande), −6 retard, −4 aléa.
    const relations = { ...(r.relationsFournisseurs || {}) };
    const bumpRelation = (fid, delta) => {
      relations[fid] = Math.max(0, Math.min(100, (relations[fid] ?? RELATION_DEFAUT) + delta));
    };
    const fournisseurMsgs = [];
    // 30/06 — Supply chain groupe : coordinateurs + plateforme logistique
    // réduisent la probabilité de retard fournisseur.
    const fiabiliteBonus = typeof get().getSupportFiabiliteFournisseur === 'function' ? get().getSupportFiabiliteFournisseur() : 0;
    const retardGroupeMult = Math.max(0.2, (1 - fiabiliteBonus) * ((s.activeInvestments || {})['grp-logistique'] ? 0.5 : 1));
    let commandes = r.commandes.map(c => {
      if (c.recu || c.jourLivraison > totalDays) return c;
      if (!c.retardTraite) {
        const fData = getFournisseur(c.fournisseurId);
        if (fData && Math.random() < probaRetardLivraison(fData, relations[c.fournisseurId] ?? RELATION_DEFAUT) * retardGroupeMult) {
          const retard = 1 + Math.floor(Math.random() * 4);
          bumpRelation(c.fournisseurId, REL_RETARD);
          let next = { ...c, retardTraite: true, retardJours: retard, jourLivraison: c.jourLivraison + retard, jourEcheance: c.jourEcheance + retard };
          if (Math.random() < ALEA_PROBA_SUR_RETARD && c.qty > 1) {
            const perteQty = Math.min(c.qty - 1, Math.max(1, Math.round(c.qty * (5 + Math.random() * 10) / 100)));
            const avoir = Math.round(perteQty * c.prixUnitaireNet * 100) / 100;
            next = { ...next, qty: c.qty - perteQty, montant: Math.round((c.montant - avoir) * 100) / 100, perteQty, avoir };
            bumpRelation(c.fournisseurId, REL_ALEA);
            fournisseurMsgs.push({ tone: 'urgent', subject: `🚚 Retard + colis endommagé — ${c.fournisseurNom}`, body: `La livraison de ${c.famNom} est décalée de ${retard} j (arrivée J${next.jourLivraison}) et ${perteQty} unités sont arrivées endommagées : avoir de ${avoir.toLocaleString('fr-FR')} € déduit de la facture. Votre relation avec ${c.fournisseurNom} se dégrade.` });
          } else {
            fournisseurMsgs.push({ tone: 'urgent', subject: `🚚 Retard de livraison — ${c.fournisseurNom}`, body: `La livraison de ${c.qty} × ${c.famNom} est décalée de ${retard} j (arrivée J${next.jourLivraison}). Votre relation avec ${c.fournisseurNom} se dégrade.` });
          }
          return next;
        }
        c = { ...c, retardTraite: true };
      }
      const st = stock[c.famId] || { qty: 0, cump: c.prixUnitaireNet };
      const newQty = st.qty + c.qty;
      const newCump = newQty > 0
        ? Math.round(((st.qty * st.cump + c.qty * c.prixUnitaireNet) / newQty) * 100) / 100
        : c.prixUnitaireNet;
      stock[c.famId] = { qty: newQty, cump: newCump };
      bumpRelation(c.fournisseurId, REL_LIVRAISON + (c.grosseCommande ? REL_GROSSE_COMMANDE : 0));
      return { ...c, recu: true };
    });

    // 2. Ventes par famille (samedi/dimanche : flux piéton +25 %).
    // SCD Lot 2 — effets de l'équipe : conversion, trafic, panier, caisses,
    // options planning (dimanche/nocturnes/primes).
    const effects = computeEquipeEffects(r, totalDays);
    // SCD Lot 3 — marketing : leviers, avis, promo, période commerciale,
    // événements en cours et attractivité des segments clients.
    const mkt = r.marketing || defaultMarketing();
    const mfx = computeMarketingEffects({ ...r, marketing: mkt }, { month, day: s.day || 1, totalDays });
    // SCD Lot 4 — zones du magasin, réseau de magasins, site web (trafic magasin).
    const magFx = computeMagasinEffects(r.magasinZones || {});
    // SCD R3 — chantiers terminés appliqués, effets des améliorations du
    // magasin principal, fermeture partielle pendant les gros travaux.
    const chP = appliquerChantiers(r.magasin, totalDays);
    const chR = (r.magasins || []).map(m => appliquerChantiers(m, totalDays));
    // Événements locaux par ville (~15 %/mois par ville implantée, ciblés sur le magasin de la ville).
    const rollEvenementLocal = (mag, principal) => {
      if (!mag) return mag;
      if (mag.evenementLocal && totalDays < mag.evenementLocal.finJour) return mag;
      const villeId = villeDuMagasin(mag, principal);
      if (!villeId || Math.random() >= PROBA_EVENEMENT_VILLE_JOUR) {
        return mag.evenementLocal ? { ...mag, evenementLocal: null } : mag;
      }
      const ev = tirerEvenementVille(villeId);
      if (!ev) return mag;
      if (typeof get().pushCustomMessage === 'function') {
        get().pushCustomMessage({
          senderType: 'system', senderName: 'SCD — Actualité locale', tone: ev.type === 'positif' ? 'positive' : 'urgent', kind: 'info',
          subject: `${ev.emoji} ${ev.nom} — ${ev.villeNom}`,
          body: `${ev.texte} Effet sur votre magasin de ${ev.villeNom} : trafic ${ev.traficMult > 1 ? '+' : ''}${Math.round((ev.traficMult - 1) * 100)} % pendant ${ev.duree_jours} jours.`,
          ttlDays: 8,
        });
      }
      return { ...mag, evenementLocal: { id: ev.id, nom: ev.nom, emoji: ev.emoji, type: ev.type, traficMult: ev.traficMult, finJour: totalDays + ev.duree_jours } };
    };
    const magasinPrincipal = rollEvenementLocal(chP.mag, true);
    let magasinsReseau = chR.map(x => rollEvenementLocal(x.mag, false));

    // ── Gestion du parc : concurrents, offres de reprise, liquidations ──
    const msg = (payload) => { if (typeof get().pushCustomMessage === 'function') get().pushCustomMessage(payload); };
    fournisseurMsgs.forEach(m => msg({ senderType: 'system', senderName: 'SCD — Approvisionnement', kind: 'info', ttlDays: 6, ...m }));
    let concurrents = r.concurrents || { implantations: [], prochainJour: 18 + Math.floor(Math.random() * 6) };
    let concurrentEvent = r.concurrentEvent || null;
    const jourN = totalDays + 1;
    // Fin de liquidation → fermeture sans indemnité.
    magasinsReseau = magasinsReseau.filter(m => {
      if (m.vente?.statut === 'liquidation' && jourN >= m.vente.finJour) {
        msg({ senderType: 'system', senderName: 'SCD — Gestion du parc', tone: 'neutral', kind: 'info', subject: `🏁 Liquidation terminée — ${m.nom}`, body: 'Le magasin a fermé ses portes après sa vente spéciale de liquidation. Aucune indemnité de bail à verser.', ttlDays: 8 });
        return false;
      }
      return true;
    });
    // Offres d'achat sur les fonds en vente + expiration.
    magasinsReseau = magasinsReseau.map(m => {
      if (m.vente?.statut !== 'en_vente') return m;
      let vente = m.vente;
      if (vente.offre && jourN >= vente.offre.expireJour) {
        msg({ senderType: 'system', senderName: 'SCD — Gestion du parc', tone: 'urgent', kind: 'info', subject: `⌛ Offre expirée — ${m.nom}`, body: `${vente.offre.acheteur} s'est retiré faute de réponse. Le magasin reste en vente.`, ttlDays: 6 });
        vente = { ...vente, offre: null };
      }
      if (!vente.offre && Math.random() < probaOffreJour(vente.prix / Math.max(1, estimationFonds(m)))) {
        const offre = genererOffre(m, jourN);
        msg({ senderType: 'system', senderName: 'SCD — Gestion du parc', tone: 'positive', kind: 'info', subject: `📨 Offre de reprise — ${m.nom}`, body: `${offre.acheteur} propose ${offre.montant.toLocaleString('fr-FR')} € pour le fonds de commerce (offre valable 3 jours). Répondez depuis Gestion du parc : accepter, contre-offre ou refuser.`, ttlDays: 5 });
        vente = { ...vente, offre };
      }
      return vente === m.vente ? m : { ...m, vente };
    });
    // Ouvertures concurrentes (~1 / 2-3 semaines, 25 % de chances de viser une de vos villes).
    if (jourN >= (concurrents.prochainJour ?? 18)) {
      const prises = new Set(concurrents.implantations.map(i => i.villeId));
      const parV = magasinsParVille({ ...r, magasins: magasinsReseau });
      const mesVilles = VILLES.filter(v => parV[v.id]?.length && !prises.has(v.id));
      const libres = VILLES.filter(v => !parV[v.id]?.length && !prises.has(v.id) && !v.international);
      const viserJoueur = Math.random() < 0.25 && mesVilles.length > 0;
      const pool = viserJoueur ? mesVilles : (libres.length ? libres : mesVilles);
      const cible = pool.length ? pool[Math.floor(Math.random() * pool.length)] : null;
      if (cible) {
        const ens = ENSEIGNES_CONCURRENTES[Math.floor(Math.random() * ENSEIGNES_CONCURRENTES.length)];
        concurrents = { implantations: [...concurrents.implantations, { villeId: cible.id, enseigneId: ens.id, jour: jourN }], prochainJour: jourN + 14 + Math.floor(Math.random() * 8) };
        const chezVous = parV[cible.id]?.length > 0;
        msg({ senderType: 'system', senderName: 'SCD — Veille concurrentielle', tone: 'urgent', kind: 'info', subject: `🏪 ${ens.nom} ouvre à ${cible.nom} !`, body: chezVous ? `Le concurrent s'installe en face de votre magasin : attendez-vous à une perte de trafic sur place (−8 %, −12 % dans la durée).` : `${cible.nom} est prise : si vous vous y implantez plus tard, vous subirez la concurrence locale. Développez-vous vite pour occuper le terrain !`, ttlDays: 10 });
      } else {
        concurrents = { ...concurrents, prochainJour: jourN + 10 };
      }
    }
    // Interactions concurrentes : guerre des prix ou rachat opportuniste (~1 %/j chacun).
    if (!concurrentEvent && concurrents.implantations.length > 0) {
      const parV2 = magasinsParVille({ ...r, magasins: magasinsReseau });
      const rnd = Math.random();
      const chezMoi = concurrents.implantations.filter(i => parV2[i.villeId]?.length && !(i.guerreFinJour > jourN));
      if (rnd < 0.011 && chezMoi.length) {
        const imp = chezMoi[Math.floor(Math.random() * chezMoi.length)];
        concurrentEvent = { type: 'guerre', villeId: imp.villeId, enseigneId: imp.enseigneId, jour: jourN };
      } else if (rnd >= 0.011 && rnd < 0.02) {
        const imp = concurrents.implantations[Math.floor(Math.random() * concurrents.implantations.length)];
        concurrentEvent = { type: 'rachat', villeId: imp.villeId, enseigneId: imp.enseigneId, jour: jourN, prix: 14000 + Math.floor(Math.random() * 20) * 500 };
      }
    }
    // ── Franchise : candidatures, redevances mensuelles, résiliations rares ──
    let franchise = r.franchise || null;
    let redevancesFranchiseJour = 0;
    if (franchise?.cree) {
      franchise = { ...franchise, franchises: [...(franchise.franchises || [])] };
      if (franchise.candidature && jourN >= franchise.candidature.expireJour) {
        msg({ senderType: 'system', senderName: 'SCD — Franchise', tone: 'urgent', kind: 'info', subject: `⌛ Candidature expirée — ${franchise.candidature.nom}`, body: `${franchise.candidature.nom} n'a pas eu de réponse et a retiré sa candidature pour ${franchise.candidature.villeNom}.`, ttlDays: 6 });
        franchise.candidature = null;
        franchise.prochainJour = jourN + delaiProchaineCandidature(franchise.droitEntree, franchise.redevancePct);
      }
      if (!franchise.candidature && jourN >= (franchise.prochainJour ?? jourN)) {
        const cand = genererCandidatFranchise({ ...r, magasins: magasinsReseau, franchise }, jourN);
        if (cand) {
          franchise.candidature = cand;
          msg({ senderType: 'system', senderName: 'SCD — Franchise', tone: 'positive', kind: 'info', subject: `🤝 Candidature de franchise — ${cand.nom}`, body: `${cand.nom} souhaite ouvrir sous votre enseigne à ${cand.villeNom} (résultat mensuel estimé : ${cand.resultatMensuel.toLocaleString('fr-FR')} €). Droit d'entrée : ${franchise.droitEntree.toLocaleString('fr-FR')} € · redevance : ${franchise.redevancePct} % du résultat. Répondez sous 6 jours depuis Développement → Franchise.`, ttlDays: 6 });
        } else {
          franchise.prochainJour = jourN + 15;
        }
      }
      // Demande d'aide d'un franchisé (animation commerciale).
      if (franchise.franchises.length > 0 && !franchise.aideEvent && Math.random() < probaAideFranchiseJour(franchise.franchises.length)) {
        const f = franchise.franchises[Math.floor(Math.random() * franchise.franchises.length)];
        franchise.aideEvent = { franchiseId: f.id, nom: f.nom, villeNom: f.villeNom, resultatMensuel: f.resultatMensuel, jour: jourN, expireJour: jourN + 5 };
        msg({ senderType: 'system', senderName: 'SCD — Franchise', tone: 'urgent', kind: 'info', subject: `🙋 ${f.nom} (${f.villeNom}) demande votre aide`, body: `Votre franchisé veut lancer une animation commerciale et sollicite l'enseigne. Répondez sous 5 jours depuis Développement → Franchise (ou la fenêtre en jeu). Votre choix impactera son résultat — et vos redevances.`, ttlDays: 5 });
      } else if (franchise.aideEvent && jourN >= franchise.aideEvent.expireJour) {
        // Ignorée = refus implicite, en plus mal vécu.
        const idx = franchise.franchises.findIndex(x => x.id === franchise.aideEvent.franchiseId);
        if (idx >= 0) {
          const f = franchise.franchises[idx];
          franchise.franchises[idx] = { ...f, resultatMensuel: Math.round(f.resultatMensuel * 0.95) };
        }
        franchise.aidesStats = { ...(franchise.aidesStats || { demandes: 0, financees: 0, refusees: 0 }) };
        franchise.aidesStats.demandes += 1;
        franchise.aidesStats.refusees += 1;
        msg({ senderType: 'system', senderName: 'SCD — Franchise', tone: 'urgent', kind: 'info', subject: `😶 ${franchise.aideEvent.nom} n'a pas eu de réponse`, body: `La demande d'aide de ${franchise.aideEvent.villeNom} est restée lettre morte : résultat mensuel −5 % et un franchisé refroidi.`, ttlDays: 6 });
        franchise.aideEvent = null;
      }
      // Redevances mensuelles (anniversaire de signature) + résiliation rare.
      const restants = [];
      franchise.franchises.forEach((f0) => {
        let f = f0;
        const anciennete = jourN - f.signeJour;
        if (anciennete > 0 && anciennete % 30 === 0) {
          const resultatMois = Math.round(f.resultatMensuel * (0.8 + Math.random() * 0.4));
          const redev = Math.round(resultatMois * (franchise.redevancePct / 100));
          redevancesFranchiseJour += redev;
          f = { ...f, resultatPrecedent: f.dernierResultat ?? null, dernierResultat: resultatMois, derniereRedevance: redev, totalRedevances: (f.totalRedevances || 0) + redev };
          if (Math.random() < PROBA_RESILIATION_MENSUELLE) {
            franchise.perdus = (franchise.perdus || 0) + 1;
            msg({ senderType: 'system', senderName: 'SCD — Franchise', tone: 'urgent', kind: 'info', subject: `📉 Résiliation — ${f.nom} (${f.villeNom})`, body: `${f.nom} met fin à son contrat de franchise à ${f.villeNom}. La ville ne compte plus dans votre couverture réseau. Redevances perçues sur ce contrat : ${f.totalRedevances.toLocaleString('fr-FR')} €.`, ttlDays: 10 });
            return;
          }
        }
        restants.push(f);
      });
      franchise.franchises = restants;
      if (redevancesFranchiseJour > 0) {
        franchise.totalRedevances = (franchise.totalRedevances || 0) + redevancesFranchiseJour;
        msg({ senderType: 'system', senderName: 'SCD — Franchise', tone: 'positive', kind: 'info', subject: `💶 Redevances de franchise : +${redevancesFranchiseJour.toLocaleString('fr-FR')} €`, body: `Vos franchisés ont versé leurs redevances mensuelles (${franchise.redevancePct} % de leur résultat).`, ttlDays: 5 });
      }
    }

    if (typeof get().pushCustomMessage === 'function') {
      [...chP.termines, ...chR.flatMap(x => x.termines)].forEach(({ amelio, niveau, magNom }) => {
        if (!amelio || !niveau) return;
        get().pushCustomMessage({
          senderType: 'system', senderName: 'SCD — Travaux', tone: 'positive', kind: 'info',
          subject: `Travaux terminés : ${amelio.nom}${niveau.niveau > 1 ? ` (niveau ${niveau.niveau})` : ''}`,
          body: `Le chantier « ${niveau.nom} »${magNom ? ` du magasin ${magNom}` : ''} est terminé. Les effets s'appliquent dès aujourd'hui.`,
          ttlDays: 10,
        });
      });
    }
    const amelFx = computeAmelFx(magasinPrincipal);
    // 03/07 — merchandising : planogramme (facing) + parcours client.
    const merchFx = computeMerchFx(r, { secteurId: r.secteur, month });
    const chantierMult = fermeturePartielle(magasinPrincipal, totalDays) ? CHANTIER_TRAFIC_MULT : 1;
    const reseau = computeReseauEffects({ ...r, magasins: magasinsReseau, concurrents }, totalDays, {
      merchMult: (secteurId) => (secteurId === r.secteur ? 1 : multGlobalSecteur(r, secteurId, month)),
    });
    const palierEc = getPalier((r.ecommerce || {}).palier || 0);
    const traficWebPct = palierEc?.trafic_magasin_pct || 0;
    const promoPriceMult = mfx.promoActive ? 1 - mfx.promoPct / 100 : 1;
    // SCD R1 — coefficients de la localisation du magasin principal
    // (trafic × panier × concurrence locale) : effets visibles dès le tour suivant.
    // Bonus géographiques : notoriété réseau (couverture + capitales) et
    // domination locale du magasin principal (variété de formats dans sa ville).
    const geo = bonusReseauGeo({ ...r, magasins: magasinsReseau });
    const geoMult = 1 + geo.totalPct / 100;
    // 30/06 — Dynamique groupe : fonctions support (+% CA) et ERP groupe.
    const grpInv = s.activeInvestments || {};
    const groupeMult = 1
      + (typeof get().getSupportRevenueMultiplier === 'function' ? get().getSupportRevenueMultiplier() : 0)
      + (grpInv['grp-erp'] ? 0.05 : 0);
    const domPrincipal = 1 + bonusDominationPct(typesVille(r, villeDuMagasin(r.magasin, true)).length) / 100;
    const locMult = (r.magasin.coefTrafic || 1) * (r.magasin.coefPanier || 1) * (r.magasin.concurrenceMalus || 1)
      * (evenementLocalActif(magasinPrincipal, totalDays)?.traficMult || 1)
      * malusConcurrence({ ...r, concurrents }, villeDuMagasin(r.magasin, true), totalDays)
      * domPrincipal;
    const marketingMult = mfx.traficTotalMult * mfx.evtConversionMult
      * (1 + (magFx.traficPct + amelFx.traficPct + traficWebPct + merchFx.traficPct) / 100) * reseau.demandeMult * locMult * chantierMult * geoMult * groupeMult;
    const weekendMult = working ? 1 : 1.25;
    const noise = 0.9 + Math.random() * 0.2;
    const equipeMult = (1 + effects.conversionBonus + mfx.conversionBonus + (magFx.conversionPct + amelFx.conversionPct + merchFx.conversionPct) / 100) * (1 + effects.traficBonus) * (1 + effects.panierBonus + mfx.panierBonus + (magFx.panierPct + amelFx.panierPct + merchFx.panierPct) / 100) * effects.caMult;

    // Attente en caisse : capacité = 100 acheteurs/j (le gérant) + 120 par hôte.
    // Si les acheteurs estimés dépassent la capacité → malus (plancher −20 %).
    let demandePrevue = 0;
    sec.familles.forEach(f => {
      const a = r.assortiment[f.id];
      if (!a || !a.actif) return;
      const conseille = prixConseille(f, r.positionnement);
      demandePrevue += demandeBaseJour(f, r.secteur, r.positionnement, r.modele, month)
        * priceAdjustment(a.prixVente, conseille, f.elasticite) * weekendMult * equipeMult * marketingMult;
    });
    const acheteursEstimes = demandePrevue / (sec.articles_par_achat || 1);
    const capaciteCaisse = 100 + effects.capaciteCaisse + magFx.capaciteCaisse + amelFx.capaciteCaisse;
    const malusCaisse = acheteursEstimes > capaciteCaisse
      ? Math.max(0.8, capaciteCaisse / acheteursEstimes)
      : 1;

    let caJour = 0;
    let margeJour = 0;
    let ventesJour = 0;
    let demandeJourTotal = 0;
    let rupturesUnites = 0;
    let ruptureCaManque = 0;
    let accisesJour = 0;
    const tvaCollecteeJour = {};
    sec.familles.forEach(f => {
      const a = r.assortiment[f.id];
      const st = stock[f.id] || { qty: 0, cump: f.prix_achat };
      if (!a || !a.actif) { ventes7[f.id] = [...(ventes7[f.id] || []), 0].slice(-7); return; }
      const conseille = prixConseille(f, r.positionnement);
      const adj = priceAdjustment(a.prixVente, conseille, f.elasticite);
      const demande = Math.round(
        demandeBaseJour(f, r.secteur, r.positionnement, r.modele, month) * adj * weekendMult * noise * equipeMult * malusCaisse * marketingMult * (merchFx.familleMult[f.id] ?? 1)
      );
      // SCD Lot 3 — promo : prix effectif réduit (trafic ↑, marge ↓).
      const prixEffectif = Math.round(a.prixVente * promoPriceMult * 100) / 100;
      let ventes = Math.min(demande, st.qty);
      let manque = demande - ventes;
      // Employé de réserve / responsable rayon : une part des ventes perdues
      // est « sauvée » (réassort express, substitution produit équivalent).
      const sauvees = Math.round(manque * effects.tauxRecupRupture);
      manque -= sauvees;
      caJour += (ventes + sauvees) * prixEffectif;
      margeJour += (ventes + sauvees) * (prixEffectif - st.cump);
      ventesJour += ventes + sauvees;
      demandeJourTotal += demande;
      rupturesUnites += manque;
      ruptureCaManque += manque * prixEffectif;
      // TVA multi-taux collectée + accises (caviste).
      const taux = String(f.tva);
      tvaCollecteeJour[taux] = (tvaCollecteeJour[taux] || 0) + (ventes + sauvees) * prixEffectif * (f.tva / 100);
      if (f.accise) accisesJour += (ventes + sauvees) * f.accise;
      stock[f.id] = { ...st, qty: st.qty - ventes };
      ventes7[f.id] = [...(ventes7[f.id] || []), ventes + sauvees].slice(-7);
    });

    // 3. Démarque sur surstock (> 45 j de couverture → 1 %/j du surplus jeté).
    // SCD Lot 2 — responsable de rayon : démarque ×0.5.
    let demarqueEuros = 0;
    let demarqueUnites = 0;
    sec.familles.forEach(f => {
      const st = stock[f.id];
      if (!st || st.qty <= 0) return;
      const couverture = couvertureJours(st.qty, ventes7[f.id]);
      if (couverture > SEUIL_SURSTOCK_JOURS && couverture !== 999) {
        const avg = (ventes7[f.id] || []).reduce((x, y) => x + y, 0) / Math.max(1, (ventes7[f.id] || []).length);
        const surplus = Math.max(0, st.qty - Math.ceil(avg * SEUIL_SURSTOCK_JOURS));
        const perte = Math.ceil(surplus * TAUX_DEMARQUE_JOUR * effects.demarqueMult * magFx.demarqueMult);
        if (perte > 0) {
          stock[f.id] = { ...st, qty: st.qty - perte };
          demarqueEuros += perte * st.cump;
          demarqueUnites += perte;
        }
      }
    });

    // SCD Lot 4 — E-commerce : visites → commandes → CA web − retours.
    // Prélève les ventes web dans le stock partagé (clone local `stock`).
    const ecTick = computeEcommerceTick(r, {
      sec, totalDays, avisMult: mfx.avisMult, promoPriceMult, stock, equipe: r.equipe,
      partCcPct: amelFx.partCcPct + magFx.partCcPct,
    });
    if (ecTick) {
      margeJour += ecTick.margeWeb;
      Object.entries(ecTick.tvaParTaux).forEach(([taux, m]) => {
        tvaCollecteeJour[taux] = (tvaCollecteeJour[taux] || 0) + m;
      });
    }

    // 4. Paiements fournisseurs dus (30/60 j après livraison) + TVA déductible.
    let paiementsFournisseurs = 0;
    let tvaDeductibleJour = 0;
    commandes = commandes.map(c => {
      if (c.recu && !c.paye && c.jourEcheance <= totalDays) {
        paiementsFournisseurs += c.montant;
        const fam = sec.familles.find(f => f.id === c.famId);
        tvaDeductibleJour += c.montant * ((fam?.tva || 20) / 100);
        return { ...c, paye: true };
      }
      return c;
    });
    // Purge des commandes payées depuis plus de 30 jours (limite la taille de la save).
    commandes = commandes.filter(c => !c.paye || (totalDays - c.jourEcheance) <= 30);

    // 5. Charges quotidiennes du magasin.
    //    Loyer + charges (+ stockage en cave pour le caviste), masse salariale,
    //    frais CB (1,2 % sur 60 % du CA), redevances franchise (CA + pub), accises.
    const stockageCave = (sec.stockage_cave_mensuel || 0) / 30;
    const loyersPercusJour = (r.biensLocatifs || []).reduce((s, b) => s + (b.loyerMensuel || 0), 0) / 30;
    const loyerJour = ((r.magasin.murs ? 0 : r.magasin.loyerMensuel) + r.magasin.chargesMensuelles) / 30 + stockageCave - loyersPercusJour;
    // 30/06 — DRH / chasseur de têtes groupe : −% masse salariale magasins.
    const masseDiscount = Math.min(0.2, typeof get().getSupportMasseSalarialeDiscount === 'function' ? get().getSupportMasseSalarialeDiscount() : 0);
    const masseJour = effects.masseSalarialeJour * (1 - masseDiscount);
    const fraisCb = caJour * PART_CB * TAUX_FRAIS_CB;
    const redevance = caJour * ((mod.redevance_pct || 0) / 100);
    const redevancePub = caJour * ((mod.redevance_pub_pct || 0) / 100);
    // SCD Lot 3 — coûts marketing quotidiens (leviers au prorata) + points
    // fidélité (% du CA) + surcoûts temporaires d'événements (% du CA).
    const coutMarketingJour = mfx.coutJour + caJour * ((mfx.margeMalusPctCa + mfx.evtSurcoutPctCa) / 100);
    // SCD Lot 4 — entretien des zones, loyers du réseau, coûts e-commerce.
    // SCD R3 — entretien des améliorations du magasin principal.
    // 30/06 — Responsable logistique groupe : −% entretien des magasins.
    const entretienDiscount = Math.min(0.4, typeof get().getSupportMaintenanceDiscount === 'function' ? get().getSupportMaintenanceDiscount() : 0);
    const coutLot4Jour = (magFx.entretienJour + amelFx.entretienJour) * (1 - entretienDiscount) + reseau.coutJour + (ecTick?.coutJour || 0);

    // 6. Fiscalité périodique (déclenchée le 1er du jour courant).
    //    • TVA : compteurs mensuels multi-taux ; reversement au 1er du mois
    //      (flux neutralisé : le client a payé TTC — simplification MVP).
    //    • 1er janvier : TASCOM (si surface > seuil) + indexation du bail (+2 %).
    const fiscal = { ...(r.fiscal || {}) };
    fiscal.tvaMoisCollectee = { ...(fiscal.tvaMoisCollectee || {}) };
    Object.entries(tvaCollecteeJour).forEach(([taux, m]) => {
      fiscal.tvaMoisCollectee[taux] = (fiscal.tvaMoisCollectee[taux] || 0) + m;
    });
    fiscal.tvaMoisDeductible = (fiscal.tvaMoisDeductible || 0) + tvaDeductibleJour;
    fiscal.accisesCumul = Math.round((fiscal.accisesCumul || 0) + accisesJour);
    fiscal.fraisCbCumul = Math.round((fiscal.fraisCbCumul || 0) + fraisCb);
    fiscal.redevancePubCumul = Math.round((fiscal.redevancePubCumul || 0) + redevancePub);
    fiscal.masseSalarialeCumul = Math.round((fiscal.masseSalarialeCumul || 0) + masseJour);

    let tascomJour = 0;
    let magasin = magasinPrincipal;
    // 04/07 — Saisonnalité du rayon : le 1er de chaque mois, alerte un mois
    // avant les bascules de demande (familles qui montent/descendent, score prévu).
    if ((s.day || 1) === 1 && totalDays > 5) {
      secteursReseau(r).forEach((secId) => {
        if (!merchSecteur(r, secId).facing) return;
        const prev = previsionSaison(r, secId, month);
        if (!prev.alerte) return;
        const secNom = getSecteur(secId)?.nom || secId;
        const fmt = (arr, signe) => arr.map(v => `${v.nom} (${signe}${Math.abs(v.pct)} %)`).join(', ');
        const parts = [];
        if (prev.montent.length) parts.push(`en hausse : ${fmt(prev.montent, '+')}`);
        if (prev.descendent.length) parts.push(`en baisse : ${fmt(prev.descendent, '−')}`);
        msg({
          senderType: 'system', senderName: 'SCD — Merchandising', tone: 'warning', kind: 'info',
          subject: `📅 ${secNom} : la saison change le mois prochain — révisez le planogramme`,
          body: `Le mois prochain, la demande ${parts.join(' ; ')}. Votre planogramme ${secNom} passerait de ${prev.scoreActuel}/100 à ${prev.scorePrevu}/100 si vous ne touchez à rien. Rendez-vous sur la page Merchandising : la suggestion automatique tient compte de la saison.`,
          ttlDays: 20,
        });
      });
    }
    if ((s.day || 1) === 1 && totalDays > 5) {
      // Reversement TVA du mois écoulé (indicatif).
      const collectee = Object.values(fiscal.tvaMoisCollectee).reduce((a, b) => a + b, 0);
      const nette = Math.max(0, Math.round(collectee - (fiscal.tvaMoisDeductible || 0)));
      fiscal.tvaDernierReversement = nette;
      fiscal.tvaVerseeCumul = (fiscal.tvaVerseeCumul || 0) + nette;
      fiscal.tvaMoisCollectee = {};
      fiscal.tvaMoisDeductible = 0;
      if ((s.month || 1) === 1) {
        // TASCOM annuelle (pédagogique : > 150 m² → 6 €/m²/an).
        if ((magasin.surface || 0) > TASCOM_SEUIL_M2) {
          tascomJour = (magasin.surface || 0) * TASCOM_EUR_M2_AN;
          fiscal.tascomCumul = (fiscal.tascomCumul || 0) + tascomJour;
        }
        // Indexation annuelle du bail commercial.
        magasin = { ...magasin, loyerMensuel: Math.round(magasin.loyerMensuel * INDEXATION_BAIL) };
      }
    }

    // SCD Lot 3 — Satisfaction (0-100), avis en ligne et habituation promo.
    // Montent : accueil/fidélité, promo. Descendent : ruptures, attente en
    // caisse, prix élevés. Rappel doux vers 55 (rien n'est jamais acquis).
    const ruptureRate = demandeJourTotal > 0 ? rupturesUnites / demandeJourTotal : 0;
    const idxPrix = indexPrixMoyen(r);
    let sat = mkt.satisfaction ?? 60;
    sat += mfx.satisfactionJour + magFx.satisfactionJour + amelFx.satisfactionJour + (ecTick?.satisfactionJour || 0) + merchFx.satisfactionJour;
    // 30/06 — fonctions support (satisfaction_passive /mois) + académie groupe.
    const satSupport = typeof get().getSupportSatisfactionPassive === 'function' ? get().getSupportSatisfactionPassive() : 0;
    sat += (satSupport + (grpInv['grp-academie'] ? 3 : 0)) / 30;
    if (mfx.promoActive) sat += 0.1;
    sat -= ruptureRate * 8;
    sat -= (1 - malusCaisse) * 10;
    if (idxPrix > 1.15) sat -= 0.25;
    sat += (55 - sat) * 0.02;
    sat = Math.min(100, Math.max(0, Math.round(sat * 10) / 10));
    const noteCible = 1 + (sat / 100) * 4;
    const noteBase = mkt.noteAvis ?? 3;
    const vitesseAvis = mkt.leviers?.reseaux ? 0.06 : 0.03;
    const noteAvis = Math.min(5, Math.max(1, Math.round((noteBase + (noteCible - noteBase) * vitesseAvis) * 100) / 100));
    const nbAvis = (mkt.nbAvis || 0) + (Math.random() < (mkt.leviers?.reseaux ? 0.8 : 0.4) ? 1 + Math.floor(Math.random() * 2) : 0);
    let habituation = (mkt.habituation || 0) + (mfx.promoActive ? PROMO_CONFIG.habituation_par_jour : -PROMO_CONFIG.decay_par_jour);
    habituation = Math.min(PROMO_CONFIG.habituation_max, Math.max(0, Math.round(habituation * 1000) / 1000));
    const newMarketing = {
      ...mkt,
      satisfaction: sat,
      noteAvis,
      nbAvis,
      habituation,
      promo: mfx.promoActive ? mkt.promo : null,
      effetsTemporaires: mfx.evts,
    };
    // SCD Lot 4 — stats e-commerce du jour + cumuls.
    const ecBase = r.ecommerce || defaultEcommerce();
    const newEcommerce = {
      ...ecBase,
      stats: {
        ...ecBase.stats,
        visites: ecTick?.visites || 0,
        commandes: ecTick?.commandes || 0,
        commandesPerdues: ecTick?.perdues || 0,
        caWeb: ecTick?.caWeb || 0,
        retoursEuros: ecTick?.retoursEuros || 0,
        caWebCumul: (ecBase.stats?.caWebCumul || 0) + (ecTick?.caWeb || 0),
        retoursCumul: (ecBase.stats?.retoursCumul || 0) + (ecTick?.retoursEuros || 0),
        commandesCumul: (ecBase.stats?.commandesCumul || 0) + (ecTick?.commandes || 0),
      },
    };

    const stats = {
      ...r.stats,
      caJour: Math.round(caJour),
      margeJour: Math.round(margeJour),
      ventesJour,
      demandeJour: demandeJourTotal,
      rupturesJourUnites: rupturesUnites,
      caCumul: (r.stats.caCumul || 0) + Math.round(caJour) + (ecTick?.caWeb || 0),
      margeCumul: (r.stats.margeCumul || 0) + Math.round(margeJour),
      ruptureUnitesCumul: (r.stats.ruptureUnitesCumul || 0) + rupturesUnites,
      ruptureCaManqueCumul: Math.round((r.stats.ruptureCaManqueCumul || 0) + ruptureCaManque),
      demarqueEurosCumul: Math.round((r.stats.demarqueEurosCumul || 0) + demarqueEuros),
      demarqueUnitesCumul: (r.stats.demarqueUnitesCumul || 0) + demarqueUnites,
      redevanceCumul: Math.round((r.stats.redevanceCumul || 0) + redevance),
      // SCD Lot 3 — décomposition du trafic du jour (pédagogie : chaque
      // multiplicateur est expliqué au joueur sur Dashboard/Marketing).
      decompoTrafic: {
        weekend: weekendMult,
        equipe: Math.round(equipeMult * 100) / 100,
        leviers: mfx.traficLeviers,
        avis: mfx.avisMult,
        segments: mfx.segmentMult,
        promo: mfx.promoTraficMult,
        periode: mfx.periodeMult,
        periodeNom: mfx.periode?.nom || null,
        evenements: Math.round(mfx.evtTraficMult * mfx.evtConversionMult * 100) / 100,
        localisation: Math.round(locMult * 100) / 100,
        reseau: reseau.demandeMult,
        ameliorations: Math.round((1 + amelFx.traficPct / 100) * 100) / 100,
        chantier: chantierMult,
        caisse: Math.round(malusCaisse * 100) / 100,
        total: Math.round(weekendMult * equipeMult * marketingMult * malusCaisse * 100) / 100,
      },
      histo: [...(r.stats.histo || []), { d: totalDays + 1, ca: Math.round(caJour), marge: Math.round(margeJour) }].slice(-180),
    };

    // Mécaniques retail pédagogiques — 1 situation tous les 5-8 jours, chacune une fois.
    const mecaState = r.mecaniques || { vues: {}, prochainJour: 5 };
    let mecaniqueActive = r.mecaniqueActive || null;
    if (!mecaniqueActive && totalDays + 1 >= (mecaState.prochainJour ?? 5)) {
      const due = prochaineMecanique({ retail: { ...r, stats }, totalDays: totalDays + 1, month });
      if (due) mecaniqueActive = { id: due.id, jour: totalDays + 1 };
    }

    set({ retail: { ...r, magasin, magasins: magasinsReseau, stock, ventes7, commandes, relationsFournisseurs: relations, stats, fiscal, marketing: newMarketing, ecommerce: newEcommerce, mecaniques: mecaState, mecaniqueActive, concurrents, concurrentEvent, franchise } });

    // 02/07 — Réapprovisionnement automatique (consignes par famille).
    executerReaproAuto(get, set);
    // 05/07 — Facing négocié : coopération commerciale fournisseurs.
    const coopFacingJour = executerFacingNegocie(get, set, { day: s.day || 1, totalDays });

    return {
      ca: Math.round(caJour) + (ecTick?.caWeb || 0) + redevancesFranchiseJour + coopFacingJour,
      decaissements: Math.round(
        paiementsFournisseurs + loyerJour + redevance + redevancePub
        + masseJour + fraisCb + accisesJour + tascomJour + coutMarketingJour + coutLot4Jour
      ),
    };
  },
});
